// covered-by: frontends/carrie/src/utils/queries/__tests__/knowledge.test.tsx
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  archiveKnowledgeRule,
  deleteKnowledgeSource,
  getEnterpriseMemories,
  getKnowledgeActivity,
  getKnowledgeRule,
  getKnowledgeRules,
  getKnowledgeSourceDownload,
  getKnowledgeSourcePresignedUrl,
  getKnowledgeSources,
  promoteKnowledgeRule,
  registerKnowledgeSource,
  rejectKnowledgeRule,
  retryKnowledgeExtraction,
  getKnowledgeGraphStatus,
  requestKnowledgeGraphBuild,
  updateKnowledgeRuleScope,
  updateKnowledgeSourceNote,
  updateKnowledgeSourceScope,
  uploadKnowledgeSourceToS3,
  type GetKnowledgeRulesPayload,
} from "../../services/knowledge";
import { isNil } from "../fp";
import { isSourceInFlight } from "../parsers/knowledge";

/** Poll cadence while a source is processing/extracting. */
const SOURCES_POLL_MS = 4000;

/////
// Sources
/////

export function useKnowledgeSourcesQuery() {
  return useQuery({
    queryKey: ["knowledge-sources"],
    queryFn: () => getKnowledgeSources({ pageSize: 100 }),
    refetchInterval: (query) => {
      const sources = query.state.data?.sources ?? [];
      return sources.some(isSourceInFlight) ? SOURCES_POLL_MS : false;
    },
  });
}

/** Full canonical upload flow: presigned-url -> direct S3 POST -> register.
 * The register body never carries an s3Key — the server reconstructs it. */
export function useUploadKnowledgeSourceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "upload"],
    async mutationFn(file: File) {
      const presigned = await getKnowledgeSourcePresignedUrl({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });
      // parseResponse returns undefined on a malformed payload — surface it
      // as a mutation error instead of crashing mid-upload.
      if (isNil(presigned)) {
        throw new Error("Failed to prepare the upload URL");
      }
      await uploadKnowledgeSourceToS3(presigned.url, presigned.fields, file);
      return registerKnowledgeSource({
        sourceId: presigned.sourceId,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function useUpdateSourceScopeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "scope"],
    mutationFn: updateKnowledgeSourceScope,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function useUpdateSourceNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "note"],
    mutationFn: updateKnowledgeSourceNote,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function useRetryExtractionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "retry-extraction"],
    mutationFn: retryKnowledgeExtraction,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
    },
  });
}

export function useDeleteSourceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-sources", "delete"],
    mutationFn: deleteKnowledgeSource,
    onSuccess(_data, sourceId) {
      queryClient.invalidateQueries({ queryKey: ["knowledge-sources"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-rules"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
      // The byte cache is keyed immutable-per-source; a deleted source's
      // bytes must not survive to render in a stale viewer.
      queryClient.removeQueries({
        queryKey: ["knowledge-source-file", sourceId],
      });
    },
  });
}

export type SourceFile = { bytes: ArrayBuffer; mimeType: string };

/**
 * The source FILE BYTES, cached per source. Caching the presigned URL alone
 * still re-downloads the whole document on every viewer mount (pdf.js /
 * SpreadsheetView fetch the full file, and rotating signed URLs defeat the
 * browser HTTP cache). Source bytes are immutable — a re-upload mints a new
 * sourceId — so a cached copy never goes stale; `gcTime` bounds memory and
 * the delete mutation evicts explicitly. Concurrent viewers (inline preview
 * + dialog) dedupe to a single download via the shared query key.
 */
export function useSourceFileQuery(sourceId: string | undefined) {
  return useQuery<SourceFile>({
    queryKey: ["knowledge-source-file", sourceId],
    enabled: !isNil(sourceId),
    staleTime: Infinity,
    gcTime: 15 * 60 * 1000,
    async queryFn() {
      const download = await getKnowledgeSourceDownload(sourceId!);
      if (isNil(download)) {
        throw new Error("Source download URL could not be resolved");
      }
      const resp = await fetch(download.url);
      if (!resp.ok) {
        throw new Error(`Source file fetch failed (${resp.status})`);
      }
      return { bytes: await resp.arrayBuffer(), mimeType: download.mimeType };
    },
  });
}

/////
// Rules
/////

export function useKnowledgeRulesQuery(args: GetKnowledgeRulesPayload) {
  return useQuery({
    queryKey: [
      "knowledge-rules",
      args.status ?? null,
      args.category ?? null,
      args.query ?? null,
      args.sourceId ?? null,
      args.pageSize ?? null,
    ],
    queryFn: () => getKnowledgeRules(args),
  });
}

/** Fetch a single rule by id — resolves a deep-linked citation whose rule
 * isn't on the loaded (paginated) Rules page, so the detail panel can open on
 * the exact cited rule. Disabled until a ruleId is present. */
export function useKnowledgeRuleQuery(ruleId: string | undefined) {
  return useQuery({
    queryKey: ["knowledge-rule", ruleId ?? null],
    enabled: !isNil(ruleId),
    queryFn: () => getKnowledgeRule(ruleId!),
  });
}

/** Imperative load-more (keyset cursor) — later pages append in panel state. */
export function useLoadMoreKnowledgeRulesMutation() {
  return useMutation({
    mutationKey: ["knowledge-rules", "load-more"],
    mutationFn: getKnowledgeRules,
  });
}

function useRuleTransitionMutation(
  key: string,
  mutationFn: (ruleId: string) => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-rules", key],
    mutationFn,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-rules"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

export function usePromoteRuleMutation() {
  return useRuleTransitionMutation("promote", promoteKnowledgeRule);
}

export function useRejectRuleMutation() {
  return useRuleTransitionMutation("reject", rejectKnowledgeRule);
}

export function useArchiveRuleMutation() {
  return useRuleTransitionMutation("archive", archiveKnowledgeRule);
}

export function useUpdateRuleScopeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-rules", "scope"],
    mutationFn: updateKnowledgeRuleScope,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["knowledge-rules"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-activity"] });
    },
  });
}

/////
// Activity
/////

export function useKnowledgeActivityQuery() {
  return useQuery({
    queryKey: ["knowledge-activity"],
    queryFn: () => getKnowledgeActivity({ pageSize: 50 }),
  });
}

export function useLoadMoreKnowledgeActivityMutation() {
  return useMutation({
    mutationKey: ["knowledge-activity", "load-more"],
    mutationFn: getKnowledgeActivity,
  });
}

/////
// Memories (hooks relocated from utils/queries/knowledge-db.tsx — the panel
// and its accumulate-pages contract are unchanged, only the endpoint moved)
/////

export type EnterpriseMemoriesQueryArgs = {
  type?: string;
  query?: string;
  pageSize?: number;
};

/** Page-1 fetch for the Memories tab. Keyed by type + query so changing either
 * refetches from the top; the panel accumulates later pages in local state. */
export function useGetEnterpriseMemoriesQuery(
  args: EnterpriseMemoriesQueryArgs,
) {
  const { type, query, pageSize } = args;
  return useQuery({
    queryKey: [
      "enterprise-memories",
      type ?? null,
      query ?? null,
      pageSize ?? null,
    ],
    queryFn: () => getEnterpriseMemories({ type, query, pageSize }),
  });
}

/** Imperative load-more for the Memories tab. */
export function useLoadMoreEnterpriseMemoriesMutation() {
  return useMutation({
    mutationKey: ["enterprise-memories", "load-more"],
    mutationFn: getEnterpriseMemories,
  });
}

/** How often the page re-reads the graph's status while a build is running. */
export const KNOWLEDGE_GRAPH_POLL_MS = 5000;

/** ...and while idle. */
export const KNOWLEDGE_GRAPH_IDLE_POLL_MS = 30000;

export function useKnowledgeGraphStatusQuery() {
  return useQuery({
    queryKey: ["knowledge-graph"],
    queryFn: getKnowledgeGraphStatus,
    // why idle polling at all: a reviewer's click and a stored memory start
    // builds this page never asked for, so polling only after its own press
    // left "Last built" stale until a reload -- the memory trigger looked
    // stuck while warhol had finished. Fast while a build runs, slow otherwise.
    refetchInterval: (query) =>
      query.state.data?.building
        ? KNOWLEDGE_GRAPH_POLL_MS
        : KNOWLEDGE_GRAPH_IDLE_POLL_MS,
    // why: the app turns focus refetch off globally; coming back to this tab
    // after saving a memory in a chat is exactly when the time has moved on.
    refetchOnWindowFocus: true,
  });
}

export function useBuildKnowledgeGraphMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["knowledge-graph", "build"],
    mutationFn: requestKnowledgeGraphBuild,
    onSuccess(status) {
      queryClient.setQueryData(["knowledge-graph"], status);
    },
  });
}
